import { getAllUsers } from '../data/userDatabase.ts';
import { useMarketplaceData } from './useMarketplaceData';
import { useContactModal } from './useContactModal';
import { useMarketplaceForms } from './useMarketplaceForms';
import { useMarketplaceModals } from './useMarketplaceModals';

export interface UseMarketplaceReturn {
  // Data from useMarketplaceData
  data: ReturnType<typeof useMarketplaceData>;
  
  // Contact functionality from useContactModal
  contact: ReturnType<typeof useContactModal>;
  
  // Form management from useMarketplaceForms
  forms: ReturnType<typeof useMarketplaceForms>;
  
  // Modal management from useMarketplaceModals
  modals: ReturnType<typeof useMarketplaceModals>;
  
  // Convenience methods
  getAllUsers: typeof getAllUsers;
}

/**
 * Master marketplace hook that provides complete marketplace functionality
 * including data management, forms, modals, and contact features.
 * 
 * This hook combines all marketplace-related hooks into a single interface,
 * providing a comprehensive solution for marketplace components.
 * 
 * @returns {UseMarketplaceReturn} Complete marketplace functionality
 * 
 * @example
 * ```tsx
 * function MarketplaceComponent({ user }: { user: User }) {
 *   const marketplace = useMarketplace();
 *   
 *   const handleSellShares = () => {
 *     marketplace.forms.openSaleForm();
 *   };
 *   
 *   const handleContactBuyers = (offer: SaleOffer) => {
 *     marketplace.contact.openContactBuyers(offer);
 *   };
 *   
 *   return (
 *     <div>
 *       {marketplace.data.sortedSalesOffers.map(offer => (
 *         <div key={offer.id}>
 *           {offer.seller} - {formatCurrency(offer.sharePrice)}
 *           {marketplace.contact.canShowContactBuyers(offer, user, marketplace.data.highestBuyRequest) && (
 *             <button onClick={() => handleContactBuyers(offer)}>
 *               Contact Buyers
 *             </button>
 *           )}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useMarketplace = (onNavigate?: (page: string) => void): UseMarketplaceReturn => {
  // Initialize all hooks
  const data = useMarketplaceData();
  const contact = useContactModal(data.salesOffers, data.buyRequests, onNavigate);
  const forms = useMarketplaceForms();
  const modals = useMarketplaceModals();

  return {
    data,
    contact,
    forms,
    modals,
    getAllUsers
  };
};

/**
 * Type-safe marketplace hook selector for specific functionality
 * Use this when you only need specific parts of the marketplace functionality
 */
export const useMarketplaceSelector = <T extends keyof UseMarketplaceReturn>(
  selector: (marketplace: UseMarketplaceReturn) => T
) => {
  const marketplace = useMarketplace();
  return selector(marketplace);
};

/**
 * Pre-configured marketplace hooks for common use cases
 */
export const useMarketplaceHooks = {
  /**
   * Use only data management functionality
   */
  useData: () => useMarketplaceData(),
  
  /**
   * Use only contact functionality with provided data
   */
  useContact: (salesOffers: ReturnType<typeof useMarketplaceData>['salesOffers'], 
              buyRequests: ReturnType<typeof useMarketplaceData>['buyRequests']) => 
    useContactModal(salesOffers, buyRequests),
  
  /**
   * Use only form management functionality
   */
  useForms: () => useMarketplaceForms(),
  
  /**
   * Use only modal management functionality
   */
  useModals: () => useMarketplaceModals()
}; 